import React, { useEffect, useRef, useState, useMemo } from 'react'
import { Box, Popover, Typography, useTheme, Checkbox, FormControlLabel } from '@mui/material'
import * as d3 from 'd3'

const MultiLineGraph = ( {
    title,
    data, // Array of objects: { date: '2023-01-01', series1: 10, series2: 20 }
    seriesConfig, // Array: [{ key: 'series1', label: 'Revenue', color: '#134fcf' }]
    xAxisKey = 'date',
    dateFormat = "%Y-%m-%d",
    showMenuBar = true,
    showLegend = true,
    xAxisLabel = "",
    height = 400
} ) => {
    const containerRef = useRef()
    const [dimensions, setDimensions] = useState( { width: 0, height: height } )
    const [selectedData, setSelectedData] = useState( null )
    const [anchorPos, setAnchorPos] = useState( { left: 0, top: 0 } )
    const [visibility, setVisibility] = useState(
        seriesConfig.reduce( ( acc, s ) => ( { ...acc, [s.key]: true } ), {} )
    )

    const theme = useTheme()
    const parseTime = d3.utcParse( dateFormat )

    // Responsive dimensions
    useEffect( () => {
        const observeTarget = containerRef.current
        const resizeObserver = new ResizeObserver( ( entries ) => {
            if ( entries[0] ) {
                setDimensions( {
                    width: entries[0].contentRect.width,
                    height: entries[0].contentRect.height || height
                } )
            }
        } )
        resizeObserver.observe( observeTarget )
        return () => resizeObserver.disconnect()
    }, [height] )

    // D3 Scales
    const { x, y, margin } = useMemo( () => {
        const m = { top: 40, right: 30, bottom: 50, left: 50 }
        const xScale = d3.scaleUtc()
            .domain( d3.extent( data, d => parseTime( d[xAxisKey] ) ) )
            .range( [m.left, dimensions.width - m.right] )

        const allValues = data.flatMap( d =>
            seriesConfig.filter( s => visibility[s.key] ).map( s => +d[s.key] )
        )

        const yScale = d3.scaleLinear()
            .domain( [0, d3.max( allValues ) * 1.1 || 10] )
            .range( [dimensions.height - m.bottom, m.top] )

        return { x: xScale, y: yScale, margin: m }
    }, [data, dimensions, visibility, seriesConfig, xAxisKey] )

    const lineGenerator = ( key ) => d3.line()
        .x( d => x( parseTime( d[xAxisKey] ) ) )
        .y( d => y( d[key] ) )
        .curve( d3.curveMonotoneX )

    return (
        <Box sx={{ width: '100%', bgcolor: 'background.paper', borderRadius: 2, p: 2, boxShadow: 1 }}>
            {/* Header / Menu Bar */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">{title}</Typography>
                {showMenuBar && (
                    <Box display="flex" gap={1}>
                        {seriesConfig.map( s => (
                            <FormControlLabel
                                key={s.key}
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={visibility[s.key]}
                                        onChange={() => setVisibility( prev => ( { ...prev, [s.key]: !prev[s.key] } ) )}
                                        sx={{ color: s.color, '&.Mui-checked': { color: s.color } }}
                                    />
                                }
                                label={<Typography variant="caption">{s.label}</Typography>}
                            />
                        ) )}
                    </Box>
                )}
            </Box>

            <Box ref={containerRef} sx={{ height: height, width: '100%', position: 'relative' }}>
                <svg width={dimensions.width} height={dimensions.height}>
                    {/* Axes */}
                    <g transform={`translate(0,${dimensions.height - margin.bottom})`} ref={node => d3.select( node ).call( d3.axisBottom( x ).ticks( 5 ) )} />
                    <g transform={`translate(${margin.left},0)`} ref={node => d3.select( node ).call( d3.axisLeft( y ).ticks( 5 ) )} />

                    {/* Lines */}
                    {seriesConfig.map( s => visibility[s.key] && (
                        <path
                            key={s.key}
                            d={lineGenerator( s.key )( data )}
                            fill="none"
                            stroke={s.color}
                            strokeWidth={2}
                        />
                    ) )}

                    {/* Hover Markers (Invisible vertical bars for interaction) */}
                    {data.map( ( d, i ) => (
                        <rect
                            key={i}
                            x={x( parseTime( d[xAxisKey] ) ) - 10}
                            y={margin.top}
                            width={20}
                            height={dimensions.height - margin.bottom - margin.top}
                            fill="transparent"
                            onMouseEnter={( e ) => {
                                setSelectedData( d )
                                setAnchorPos( { left: e.pageX, top: e.pageY } )
                            }}
                            onMouseLeave={() => setSelectedData( null )}
                        />
                    ) )}
                </svg>

                {/* Popover */}
                <Popover
                    open={Boolean( selectedData )}
                    anchorReference="anchorPosition"
                    anchorPosition={anchorPos}
                    sx={{ pointerEvents: 'none' }}
                >
                    <Box sx={{ p: 1.5 }}>
                        <Typography variant="subtitle2" fontWeight="bold">{selectedData?.[xAxisKey]}</Typography>
                        {seriesConfig.map( s => visibility[s.key] && (
                            <Typography key={s.key} variant="caption" display="block" sx={{ color: s.color }}>
                                {s.label}: {selectedData?.[s.key]}
                            </Typography>
                        ) )}
                    </Box>
                </Popover>
            </Box>
        </Box>
    )
}

export default MultiLineGraph